import { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import { api } from '@/lib/api';
import { saveFailureMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/cn';
import { WATER_COLOR_OPTIONS } from '@/lib/constants';
import { qk } from '@/lib/queryKeys';
import type { ColorTierName, ColorTiers } from '@/lib/waterColor';

type Choice = ColorTierName | 'none';

const CHOICES: { value: Choice; label: string; on: string }[] = [
  { value: 'none', label: 'No marker', on: 'border-gray-500 bg-gray-100 text-gray-900' },
  { value: 'good', label: 'Good', on: 'border-emerald-600 bg-emerald-100 text-emerald-900' },
  { value: 'watch', label: 'Watch', on: 'border-amber-500 bg-amber-100 text-amber-900' },
  { value: 'bad', label: 'Bad', on: 'border-red-600 bg-red-100 text-red-900' },
];

const wordLabel = (word: string) => WATER_COLOR_OPTIONS.find((o) => o.value === word)?.label ?? word;

export function WaterColorEditor({ farmId, canEdit }: { farmId: string; canEdit: boolean }) {
  const { data, isLoading, isError } = useFarmThresholds(farmId);
  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <LoadingSpinner />
        </Card.Body>
      </Card>
    );
  }
  if (isError || !data) return null;
  const tiers = data.water_color_tiers ?? {};
  return <Editor key={JSON.stringify(tiers)} farmId={farmId} tiers={tiers} canEdit={canEdit} />;
}

function Editor({ farmId, tiers, canEdit }: { farmId: string; tiers: ColorTiers; canEdit: boolean }) {
  const queryClient = useQueryClient();
  // A colour word stored for the farm that the entry list does not offer stays editable, never silently dropped.
  const words = [...WATER_COLOR_OPTIONS.map((o) => o.value), ...Object.keys(tiers).filter((w) => !WATER_COLOR_OPTIONS.some((o) => o.value === w))];
  const [draft, setDraft] = useState<ColorTiers>(tiers);
  const dirty = JSON.stringify(draft) !== JSON.stringify(tiers);

  const save = useMutation({
    mutationFn: (water_color_tiers: ColorTiers) => api.put(`/api/v1/farms/${farmId}/settings`, { water_color_tiers }),
    // A toast, not an inline note: saving changes the server's map, which remounts this editor and would drop the note.
    onSuccess: () => {
      toast.success('Water colour markers saved');
      return queryClient.invalidateQueries({ queryKey: qk.thresholds.farm(farmId) });
    },
  });

  const choose = (word: string, choice: Choice) =>
    setDraft((d) => {
      const next = { ...d };
      if (choice === 'none') delete next[word];
      else next[word] = choice;
      return next;
    });

  return (
    <Card>
      <Card.Header>
        <Card.Title>Water colour markers</Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="mb-3 text-sm text-gray-500">
          Which water colours are good, worth watching or bad is your farm's call. A colour with no marker shows no
          colour on the overview and entry screens.
          {!canEdit && ' Only a Farm Owner can change these.'}
        </p>
        <ul className="divide-y divide-gray-100" data-water-colors>
          {words.map((word) => {
            const current: Choice = draft[word] ?? 'none';
            return (
              <li key={word} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-center sm:justify-between" data-water-color={word}>
                <span className="text-sm font-medium text-gray-900">{wordLabel(word)}</span>
                <div role="radiogroup" aria-label={`${wordLabel(word)} marker`} className="grid grid-cols-4 gap-1.5 sm:w-96">
                  {CHOICES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      role="radio"
                      aria-checked={current === c.value}
                      disabled={!canEdit}
                      onClick={() => choose(word, c.value)}
                      className={cn(
                        'min-h-[44px] rounded-lg border px-1 text-[13px] font-semibold touch-manipulation disabled:opacity-60',
                        current === c.value ? c.on : 'border-gray-300 bg-white text-gray-600'
                      )}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
        {canEdit && (
          <div className="mt-4 flex items-center justify-end gap-3">
            {save.isError && (
              <span role="alert" className="text-sm text-red-600">
                Not saved: {saveFailureMessage(save.error, 'try again')}
              </span>
            )}
            <Button onClick={() => save.mutate(draft)} disabled={!dirty || save.isPending} loading={save.isPending} className="min-h-[44px]">
              Save water colours
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
